import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Crown, Scissors, Star, Sparkles, Award, Clock } from "lucide-react";
import { api } from "@/lib/api";
import SectionHeading from "@/components/SectionHeading";
import { SITE } from "@/lib/site";

export default function Home() {
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/services").then((r) => setServices(r.data)).catch(() => {}),
      api.get("/barbers").then((r) => setBarbers(r.data)).catch(() => {}),
      api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {}),
      api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {}),
    ]);
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_monarch-style/artifacts/q8uwjzrq_d88aadf7-04d0-4ce3-849c-92372d581063.jpeg"
            alt="Monarch Cutz interior — hexagon LED ceiling and barber chairs"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-monarch-black via-monarch-black/70 to-monarch-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-monarch-black via-transparent to-monarch-black/60" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 animate-fade-up">
            <div className="eyebrow mb-7" data-testid="hero-eyebrow">
              <span className="gold-line" />Est. 2018 · Los Angeles
            </div>
            <h1
              data-testid="hero-title"
              className="font-serif text-[14vw] md:text-[8vw] lg:text-[6.5vw] leading-[0.92] tracking-tight text-monarch-cream"
            >
              The throne <br />
              <span className="italic text-monarch-gold">is waiting.</span>
            </h1>
            <p data-testid="hero-subtitle" className="mt-8 max-w-xl text-monarch-cream/80 text-lg leading-relaxed">
              A modern barbershop built on old-world ritual — sharp lines, hot towels,
              quiet luxury, and a chair that feels like it was carved for you.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/book" data-testid="hero-book-button" className="btn-gold">
                Book a Chair <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={SITE.phoneHref} data-testid="hero-call-button" className="btn-outline-gold">
                Or Call {SITE.phone}
              </a>
            </div>
          </div>

          <div className="lg:col-span-4 hidden lg:block">
            <div className="border-l border-monarch-gold/30 pl-8 space-y-7">
              <Stat icon={<Award className="w-5 h-5" />} label="Master Barbers" value="02" />
              <Stat icon={<Scissors className="w-5 h-5" />} label="Cuts a Month" value="1,200+" />
              <Stat icon={<Star className="w-5 h-5" />} label="Average Rating" value="4.97" />
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-monarch-gold/15 bg-monarch-black/70 backdrop-blur">
          <div className="overflow-hidden py-4">
            <div className="animate-marquee flex whitespace-nowrap gap-12 text-monarch-gold/70 text-xs uppercase tracking-[0.4em]">
              {[...Array(2)].flatMap((_, j) =>
                ["Precision Cuts", "Royal Shaves", "Beard Sculpting", "Hot Towel Service", "Walk-Ins Welcome", "By Appointment"].map((t, i) => (
                  <span key={`${j}-${i}`} className="flex items-center gap-12">
                    {t} <Crown className="w-3 h-3" />
                  </span>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="relative py-28 px-6 md:px-10 bg-monarch-black">
        <div className="mx-auto max-w-[1400px]">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
            <div className="md:col-span-7">
              <SectionHeading
                eyebrow="The Menu"
                title="Sharp service. Steady hands."
                subtitle="Every cut starts with a conversation. Every shave ends with a hot towel. Choose your ritual."
                testIdBase="services"
              />
            </div>
            <div className="md:col-span-5 md:text-right">
              <Link to="/book" data-testid="services-book-link" className="btn-outline-gold">View All & Book <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <article
                key={s.id}
                data-testid={`service-card-${s.id}`}
                className="card-monarch p-8 group relative"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="flex items-start justify-between mb-5">
                  <span className="text-monarch-gold/40 font-serif text-3xl">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-monarch-gold font-serif text-2xl">${s.price}</span>
                </div>
                <h3 className="font-serif text-2xl text-monarch-cream mb-3">{s.name}</h3>
                <p className="text-monarch-ash text-sm leading-relaxed mb-5 min-h-[3rem]">{s.description}</p>
                <div className="flex items-center justify-between pt-5 border-t border-monarch-gold/15">
                  <span className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-monarch-ash">
                    <Clock className="w-3.5 h-3.5" /> {s.duration}
                  </span>
                  <Link to="/book" data-testid={`service-book-${s.id}`} className="text-xs uppercase tracking-[0.22em] text-monarch-gold hover:text-monarch-champagne flex items-center gap-2">
                    Book <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="relative py-28 px-6 md:px-10 bg-monarch-ink">
        <div className="mx-auto max-w-[1400px] grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <SectionHeading
              eyebrow="About the House"
              title="Where craft meets royalty."
              testIdBase="about"
            />
            <div className="space-y-5 text-monarch-cream/80 leading-relaxed">
              <p>Monarch Cutz was founded on a simple belief: a great haircut is a ceremony. Not a transaction. Not a quick stop.</p>
              <p>Our chairs are leather. Our blades are sharp. Our music is right. And our barbers — each one a master of their craft — treat every client like the king they came in as, and send them out looking the part.</p>
              <p className="text-monarch-gold font-serif italic text-xl pt-3">&ldquo;Stay sharp. Sit tall. The crown looks good on you.&rdquo;</p>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-monarch-gold/15 pt-8">
              <Stat icon={<Crown className="w-5 h-5" />} label="Years Open" value="7" />
              <Stat icon={<Sparkles className="w-5 h-5" />} label="5-Star Reviews" value="2.1k" />
              <Stat icon={<Award className="w-5 h-5" />} label="Awards" value="11" />
            </div>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="img-zoom aspect-[3/4] mt-10">
                <img src="https://customer-assets.emergentagent.com/job_monarch-style/artifacts/so04f10h_d88aadf7-04d0-4ce3-849c-92372d581063.jpeg" alt="Monarch Cutz storefront, Los Angeles" className="w-full h-full object-cover" />
              </div>
              <div className="img-zoom aspect-[3/4]">
                <img src="https://customer-assets.emergentagent.com/job_monarch-style/artifacts/3smza49g_1e75a5cb-f28a-440f-9c8c-d3f1e17eccfb.jpeg" alt="Monarch Cutz interior — hex LED ceiling" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BARBERS PREVIEW */}
      <section className="relative py-28 px-6 md:px-10 bg-monarch-black">
        <div className="mx-auto max-w-[1400px]">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <SectionHeading eyebrow="The House" title="Meet the barbers." testIdBase="home-barbers" />
            <Link to="/barbers" data-testid="home-barbers-link" className="btn-outline-gold">All Barbers <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {barbers.map((b) => (
              <Link
                key={b.id}
                to="/barbers"
                data-testid={`home-barber-${b.id}`}
                className="group block card-monarch"
              >
                <div className="img-zoom aspect-[4/5] grayscale transition-all duration-700">
                  <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-monarch-gold mb-2">{b.role}</div>
                  <h3 className="font-serif text-2xl text-monarch-cream">{b.name}</h3>
                  <p className="text-sm text-monarch-ash mt-2">{b.specialty}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="relative py-28 px-6 md:px-10 bg-monarch-ink">
        <div className="mx-auto max-w-[1400px]">
          <SectionHeading
            eyebrow="The Gallery"
            title="Work that speaks for itself."
            subtitle="A selection of recent cuts, shaves and shop moments."
            align="center"
            testIdBase="gallery"
          />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5 mt-10">
            {gallery.map((src, i) => (
              <div key={i} data-testid={`gallery-item-${i}`} className={`img-zoom ${i % 4 === 0 ? "aspect-[3/4]" : "aspect-square"} ${i % 5 === 0 ? "md:row-span-2 md:aspect-[3/5]" : ""}`}>
                <img src={src} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative py-28 px-6 md:px-10 bg-monarch-black">
        <div className="mx-auto max-w-[1400px]">
          <SectionHeading
            eyebrow="The Word"
            title="What the regulars say."
            align="center"
            testIdBase="testimonials"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {testimonials.map((t, i) => (
              <div key={i} data-testid={`testimonial-${i}`} className="card-monarch p-7">
                <div className="flex gap-1 text-monarch-gold mb-4">
                  {[...Array(t.rating)].map((_, k) => (<Star key={k} className="w-4 h-4 fill-monarch-gold" />))}
                </div>
                <p className="text-monarch-cream/85 leading-relaxed mb-5 text-sm">&ldquo;{t.text}&rdquo;</p>
                <p className="text-monarch-gold text-xs tracking-[0.25em] uppercase">— {t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 px-6 md:px-10 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.pexels.com/photos/5970246/pexels-photo-5970246.jpeg?w=1600" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-monarch-black/85" />
        </div>
        <div className="relative z-10 mx-auto max-w-3xl text-center">
          <Crown className="w-12 h-12 text-monarch-gold mx-auto mb-7" strokeWidth={1.2} />
          <h2 className="section-title text-4xl md:text-5xl lg:text-6xl text-monarch-cream">Ready for your crown?</h2>
          <p className="mt-5 text-monarch-ash leading-relaxed">Book your chair online in under a minute. Walk in. Sit down. Leave looking unfairly good.</p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link to="/book" data-testid="cta-book-button" className="btn-gold">Book Now <ArrowRight className="w-4 h-4" /></Link>
            <a href={SITE.phoneHref} data-testid="cta-call-button" className="btn-outline-gold">Call {SITE.phone}</a>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div>
      <div className="text-monarch-gold mb-2">{icon}</div>
      <div className="font-serif text-3xl text-monarch-cream">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-monarch-ash mt-1">{label}</div>
    </div>
  );
}
